import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { API_BASE_URL } from '../../constants';
import './SignUpPage.css';

function SignUpPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // 'form', 'verify', 'complete'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');

  // Verification
  const [verificationCode, setVerificationCode] = useState('');
  const [expectedCode, setExpectedCode] = useState('');
  const [tempUserData, setTempUserData] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    if (!firstName.trim()) return 'First name is required';
    if (!lastName.trim()) return 'Last name is required';
    if (!email.trim()) return 'Email is required';
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters';
    if (password !== confirmPassword) return 'Passwords do not match';
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    
    // Phone validation (optional but if provided, should be valid)
    if (phone && !/^[\d\s\-+()]+$/.test(phone)) {
      return 'Please enter a valid phone number';
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Send verification email with 6-digit code
      const response = await fetch(`${API_BASE_URL}/api/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, firstName }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send verification email');
      }

      // Store the expected code (in production, this should be validated server-side)
      setExpectedCode(result.code);
      setTempUserData({
        firstName,
        lastName,
        email,
        phone,
        password,
        company,
        role,
        profileImage,
      });
      setStep('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    if (verificationCode !== expectedCode) {
      setError('Invalid verification code. Please try again.');
      return;
    }

    setLoading(true);

    try {
      // Create the Firebase user
      const credential = await createUserWithEmailAndPassword(
        auth,
        tempUserData.email,
        tempUserData.password
      );

      const user = credential.user;
      const displayName = `${tempUserData.firstName} ${tempUserData.lastName}`;

      // Update profile with display name
      await updateProfile(user, { displayName });

      // Upload profile image if provided
      let profileImageUrl = '';
      if (tempUserData.profileImage) {
        const formData = new FormData();
        formData.append('image', tempUserData.profileImage);
        formData.append('userId', user.uid);

        const uploadResponse = await fetch(`${API_BASE_URL}/api/upload-profile-image`, {
          method: 'POST',
          body: formData,
        });

        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          profileImageUrl = uploadResult.imageUrl;
        }
      }

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        firstName: tempUserData.firstName,
        lastName: tempUserData.lastName,
        displayName,
        email: tempUserData.email,
        phone: tempUserData.phone || '',
        company: tempUserData.company || '',
        role: tempUserData.role || '',
        profileImageUrl,
        emailVerified: true,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      });

      setStep('complete');

      // Redirect to profile after a short delay
      setTimeout(() => {
        navigate(`/user?id=${encodeURIComponent(user.uid)}`, { replace: true });
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: tempUserData.email, firstName: tempUserData.firstName }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to resend code');
      }

      setExpectedCode(result.code);
      setError('New code sent! Check your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'complete') {
    return (
      <div className="signup-shell">
        <div className="signup-card signup-card--success">
          <div className="signup-success-icon">✓</div>
          <h1>Account Created!</h1>
          <p>Your email has been verified successfully.</p>
          <p>Redirecting to your profile...</p>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="signup-shell">
        <Link to="/login" className="signup-back">
          ← Back to login
        </Link>
        <div className="signup-card">
          <h1>Verify Your Email</h1>
          <p>We've sent a 6-digit code to <strong>{tempUserData?.email}</strong></p>

          <form onSubmit={handleVerify} className="signup-form">
            <label>
              Verification Code
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="verification-input"
                disabled={loading}
                autoFocus
              />
            </label>

            {error && (
              <div className={`signup-message ${error.includes('sent') ? 'signup-message--success' : 'signup-message--error'}`}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="signup-btn signup-btn--primary">
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>

            <button
              type="button"
              onClick={resendCode}
              disabled={loading}
              className="signup-btn signup-btn--ghost"
            >
              Resend Code
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-shell">
      <Link to="/login" className="signup-back">
        ← Back to login
      </Link>
      <div className="signup-card">
        <h1>Create Account</h1>
        <p>Join Origami Robotics to start building.</p>

        <form onSubmit={handleSubmit} className="signup-form">
          <div className="signup-row">
            <label>
              First Name *
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                disabled={loading}
                required
              />
            </label>
            <label>
              Last Name *
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                disabled={loading}
                required
              />
            </label>
          </div>

          <label>
            Email *
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
              required
            />
          </label>

          <label>
            Phone Number
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
              disabled={loading}
            />
          </label>

          <div className="signup-row">
            <label>
              Company (Optional)
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Inc."
                disabled={loading}
              />
            </label>
            <label>
              Role (Optional)
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Engineer"
                disabled={loading}
              />
            </label>
          </div>

          <label>
            Password *
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              disabled={loading}
              required
            />
          </label>

          <label>
            Confirm Password *
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              disabled={loading}
              required
            />
          </label>

          <label className="signup-image-label">
            Profile Picture (Optional)
            <div className="signup-image-upload">
              {profileImagePreview ? (
                <img src={profileImagePreview} alt="Profile preview" className="signup-image-preview" />
              ) : (
                <div className="signup-image-placeholder">
                  <span>📷</span>
                  <span>Click to upload</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={loading}
              />
            </div>
          </label>

          {error && (
            <div className="signup-message signup-message--error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="signup-btn signup-btn--primary">
            {loading ? 'Sending verification...' : 'Create Account'}
          </button>
        </form>

        <p className="signup-footer">
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      </div>
    </div>
  );
}

export default SignUpPage;
